import { useAsync, IfPending, IfRejected, IfFulfilled } from "react-async";
import axios from "axios";
import { pdfjs, Document, Page } from "react-pdf";
import React, { useState } from "react";
import styled from "styled-components";
import BasicLoader from "components/UI/Loader/BasicLoader";
import SaveIcon from "components/Icon/Save";
import AddIcon from "components/Icon/Add";
import MinusIcon from "components/Icon/Minus";
import theme from "theme";
import { fetchPdf } from "api";
import ErrorRetry from "components/UI/ErrorRetry";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Container = styled.div`
  background: var(--palesky);

  .document {
    box-shadow: inset 0 3px 10px 0px rgba(0, 0, 0, 0.3);
    padding: 50px 0 0 0;
    width: 100%;
    max-width: calc(100vw - 48px - var(--nav-width));
    overflow: scroll;
  }
  .document .react-pdf__Page__canvas {
    margin: 0 auto;
  }

  .action-bar {
    width: 100%;
    background: var(--white);
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 15px;
  }
  .action {
    cursor: pointer;
    margin: 0 0 0 20px;
    height: 1rem;
    width: 1rem;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: var(--white);
  position: absolute;
  left: 0;
  top: 0;
`;

const loader = (
  <LoaderWrapper>
    <BasicLoader />
  </LoaderWrapper>
);

const PdfJsRenderer = ({ workItemId, type, className }) => {
  const [pages, setPages] = useState(0);
  const [pdfData, setPdfData] = useState();
  const [initialReRender, setInitialReRender] = useState(false);

  const source = axios.CancelToken.source();
  const asyncState = useAsync({
    promiseFn: fetchPdf,
    onReject: err => err.name === "AbortError" && source.cancel(err.message),
    onResolve: async response => {
      const array = await new Response(response.data).arrayBuffer();
      setPdfData({ data: array });
    },
    cancelToken: source.token,
    workItemId,
    type,
    watch: type
  });
  const { data: response, reload } = asyncState;
  const [zoom, setZoom] = useState(1);
  const zoomIn = () => setZoom(val => val + 0.5);
  const zoomOut = () => setZoom(val => val - 0.5);

  const saveDocument = () => {
    window.navigator.msSaveBlob(response.data, `FeeReport-${workItemId}.pdf`);
  };

  const onDocumentLoaded = ({ numPages }) => {
    setPages(numPages);
    setInitialReRender(false);
  };

  const onPageRendered = () => {
    // Workaround for characters not properly rendered initially
    if (!initialReRender) {
      setZoom(1.5);
      setInitialReRender(true);
    }
  };

  return (
    <>
      <IfPending state={asyncState}>{loader}</IfPending>
      <IfRejected state={asyncState}>
        <ErrorRetry
          message="There was an error while trying to fetch the Document. Please try again"
          onRetry={reload}
        />
      </IfRejected>
      <IfFulfilled state={asyncState}>
        <Container className={className}>
          <div className="action-bar">
            <AddIcon
              title="Zoom In"
              className="action"
              fill={theme.colors.solera}
              onClick={zoomIn}
            />
            <MinusIcon
              title="Zoom Out"
              className="action"
              fill={theme.colors.solera}
              onClick={zoomOut}
            />
            <SaveIcon
              title="Download"
              className="action"
              fill={theme.colors.solera}
              onClick={saveDocument}
            />
          </div>
          <Document
            onLoadSuccess={onDocumentLoaded}
            loading={loader}
            className="document"
            file={pdfData}
          >
            {[...Array(pages)].map((_, i) => (
              <Page
                key={`pdf_page${i + 1}`}
                pageNumber={i + 1}
                scale={zoom}
                onRenderSuccess={onPageRendered}
              />
            ))}
          </Document>
        </Container>
      </IfFulfilled>
    </>
  );
};

export default PdfJsRenderer;
